import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const alertVariants = cva(
  `tw-relative tw-w-[calc(100%-var(--spacing)*8)] tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-sm tw-grid tw-items-center
   has-[>svg]:tw-grid-cols-[calc(var(--spacing)*5)_1fr] tw-grid-cols-[0_1fr] tw-gap-y-0.5 [&>svg]:tw-size-6 tw-border-solid
   [&>svg]:tw-translate-y-0.5 [&>svg]:tw-text-current has-[>svg]:tw-gap-x-3 [&:not(:last-child)]:tw-mb-3`,
  {
    variants: {
      variant: {
        default: 'tw-bg-card tw-text-card-foreground',
        error:
          `tw-border-alert-error-danger tw-text-alert-error-danger tw-bg-transparent
          tw-shadow-none [&>svg]:tw-text-alert-error-danger
          [&_*[data-slot=alert-description]]:tw-text-alert-error-danger`,
        info:
          `tw-border-sky-500/50 tw-text-sky-700 tw-bg-sky-50 [&>svg]:tw-text-sky-600
          [&_*[data-slot=alert-description]]:tw-text-sky-600
          dark:tw-bg-sky-950/50 dark:tw-border-sky-500/40 dark:tw-text-sky-200
          dark:[&>svg]:tw-text-sky-300
          dark:[&_*[data-slot=alert-description]]:tw-text-sky-300`,
        success:
          `tw-border-emerald-500/50 tw-text-emerald-700 tw-bg-emerald-50 [&>svg]:tw-text-emerald-600
          [&_*[data-slot=alert-description]]:tw-text-emerald-600
          dark:tw-bg-emerald-950/50 dark:tw-border-emerald-500/40 dark:tw-text-emerald-200
          dark:[&>svg]:tw-text-emerald-300
          dark:[&_*[data-slot=alert-description]]:tw-text-emerald-300`,
        warning:
          `tw-border-amber-500/50 tw-text-amber-700 tw-bg-amber-50 [&>svg]:tw-text-amber-600
          [&_*[data-slot=alert-description]]:tw-text-amber-600
          dark:tw-bg-amber-950/50 dark:tw-border-amber-500/40 dark:tw-text-amber-200
          dark:[&>svg]:tw-text-amber-300
          dark:[&_*[data-slot=alert-description]]:tw-text-amber-300`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'tw-col-start-2 tw-text-lg tw-line-clamp-1 tw-min-h-4 tw-font-medium tw-tracking-tight',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'tw-text-muted-foreground tw-col-start-2 tw-gap-1 tw-text-base [&_p]:tw-leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
